import { enableMapSet } from 'immer';

import {
    type AccessStateSlice,
    accessReducer,
    INITIAL_STATE as accessInitialState
} from 'app/store/access/reducer';
import {
    accessControlsReducer,
    INITIAL_STATE as accessControlsInitialState
} from 'app/store/accessControls/reducer.js';
import {
    addViewsReducer,
    INITIAL_STATE as addViewsInitialState
} from 'app/store/addViews/reducer.js';
import { adminReducer, INITIAL_STATE as adminInitialState } from 'app/store/admin/reducer.js';
import {
    applicationConfigReducer,
    INITIAL_STATE as applicationConfigInitialState
} from 'app/store/applicationConfig/reducer.js';
import {
    attributesReducer,
    INITIAL_STATE as attributesInitialState
} from 'app/store/attributes/reducer.js';
import { blendReducer, INITIAL_STATE as blendInitialState } from 'app/store/blend/reducer.js';
import {
    materializeReducer,
    INITIAL_STATE as materializeInitialState
} from 'app/store/materialize/reducer.js';
import {
    columnStatisticsReducer,
    INITIAL_STATE as columnStatisticsInitialState
} from 'app/store/columnStatistics/reducer.js';
import {
    columnManagementReducer,
    type ColumnManagementSlice,
    INITIAL_STATE as columnManagementInitialState
} from 'app/store/columnManagement/reducer';
import {
    entitiesReducer,
    INITIAL_STATE as entitiesInitialState
} from 'app/store/entities/reducer.js';
import { errorReducer, INITIAL_STATE as errorInitialState } from 'app/store/error/reducer.js';
import {
    exchangeSourceReducer,
    INITIAL_STATE as exchangeSourceInitialState
} from 'app/store/exchangeSource/reducer.js';
import {
    exploreDataReducer,
    INITIAL_STATE as exploreDataInitialState
} from 'app/store/exploreData/reducer.js';
import { filterReducer, INITIAL_STATE as filterInitialState } from 'app/store/filter/reducer.js';
import { fileUpload } from 'app/store/fileUpload/reducer.js';
import {
    findDatasetsReducer,
    INITIAL_STATE as findDatasetsInitialState
} from 'app/store/findDatasets/reducer.js';
import { flowReducer, INITIAL_STATE as flowInitialState } from 'app/store/flow/reducer.js';
import {
    formulaEditorReducer,
    INITIAL_STATE as formulaEditorInitialState
} from 'app/store/formulaEditor/reducer.js';
import {
    dbtReducer,
    INITIAL_STATE as dbtInitialState,
    type DbtStateSlice
} from 'app/store/dbt/reducer';
import {
    glossaryReducer,
    INITIAL_STATE as glossaryInitialState
} from 'app/store/glossary/reducer.js';
import {
    integrationsReducer,
    INITIAL_STATE as integrationsInitialState
} from 'app/store/integrations/reducer.js';
import {
    githubReducer,
    INITIAL_STATE as githubInitialState,
    type GithubStateSlice
} from 'app/store/integrations/github/reducer';
import {
    type HealthcheckSliceType,
    healthcheckReducer,
    INITIAL_STATE as healthcheckInitialState
} from 'app/store/healthcheck/reducer';
import {
    jobsReducer,
    INITIAL_STATE as jobsInitialState,
    type JobsReduxSlice
} from 'app/store/jobs/reducer';
import {
    notificationsReducer,
    INITIAL_STATE as notificationsInitialState
} from 'app/store/notifications/reducer.js';
import {
    operationsReducer,
    INITIAL_STATE as operationsInitialState
} from 'app/store/operations/reducer.js';
import {
    type PreferencesSlice,
    preferencesReducer,
    INITIAL_STATE as preferencesInitialState
} from 'app/store/preferences/reducer';
import {
    deployViewReducer,
    INITIAL_STATE as deployViewInitialState
} from 'app/store/deployView/reducer.js';
import {
    downloadViewReducer,
    INITIAL_STATE as downloadViewInitialState
} from 'app/store/downloadView/reducer.js';
import { searchReducer, INITIAL_STATE as searchInitialState } from 'app/store/search/reducer.js';
import {
    projectHistoryReducer,
    INITIAL_STATE as projectHistoryInitialState,
    type ProjectHistorySlice
} from 'app/store/projectHistory/reducer';
import {
    projectsReducer,
    INITIAL_STATE as projectsInitialState
} from 'app/store/projects/reducer.js';
import {
    type SqlEditorStateSlice,
    sqlEditorReducer,
    INITIAL_STATE as sqlEditorInitialState
} from 'app/store/sqlEditor/reducer';
import {
    snowflakeReducer,
    INITIAL_STATE as snowflakeInitialState
} from 'app/store/snowflake/reducer.js';
import {
    type SnowflakeConfigurationSlice,
    snowflakeConfigurationReducer,
    INITIAL_STATE as snowflakeConfigurationInitialState
} from 'app/store/snowflakeConfiguration/reducer';
import {
    summarizeReducer,
    INITIAL_STATE as summarizeInitialState
} from 'app/store/summarize/reducer.js';
import {
    usersReducer,
    userAuthReducer,
    USERS_INITIAL_STATE as usersInitialState,
    USER_AUTH_INITIAL_STATE as userAuthInitialState
} from 'app/store/users/reducer.js';
import { viewReducer, INITIAL_STATE as viewInitialState } from 'app/store/view/reducer.js';
import {
    type AssetsSlice,
    assetsReducer,
    INITIAL_STATE as assetsInitialState
} from 'app/store/assets/reducer';
import {
    type WebsocketStatusSlice,
    websocketStatusReducer,
    INITIAL_STATE as websocketStatusInitialState
} from 'app/store/websocketStatus/reducer';
import {
    type StartupSlice,
    startupReducer,
    INITIAL_STATE as startupInitialState
} from 'app/store/startup/reducer';
import {
    transformSqlReducer,
    INITIAL_STATE as transformSqlInitialState
} from 'app/store/transformSql/reducer.js';
import {
    deploymentHistoryReducer,
    type DeploymentHistoryStateSliceType,
    INITIAL_STATE as deploymentHistoryInitialState
} from 'app/store/deploymentHistory/reducer';
import { sharesReducer, INITIAL_STATE as sharesInitialState } from 'app/store/shares/reducer.js';
import {
    type ColumnWidthState,
    columnWidthsReducer,
    INITIAL_STATE as columnWidthsInitialState
} from 'app/store/columnWidths/reducer';
import {
    type DeploymentsStateSlice,
    deploymentsReducer,
    INITIAL_STATE as deploymentsInitialState
} from 'app/store/deployments/reducer';
import {
    type ColumnFilterSlice,
    columnFilterReducer,
    INITIAL_STATE as columnFilterInitialState
} from 'app/store/columnFilter/reducer';
import { unionReducer, INITIAL_STATE as unionInitialState } from 'app/store/union/reducer.js';
import {
    extractJsonReducer,
    INITIAL_STATE as extractJsonInitialState
} from 'app/store/extractJson/reducer.js';
import {
    schemaUsageReducer,
    INITIAL_STATE as schemaUsageInitialState
} from 'app/store/schemaUsage/reducer.js';
import {
    tableDetailsReducer,
    INITIAL_STATE as tableDetailsInitialState
} from 'app/store/tableDetails/reducer.js';
import {
    notebookReducer,
    INITIAL_STATE as notebookInitialState
} from 'app/store/notebook/reducer.js';
import {
    snowflakeQueriesReducer,
    INITIAL_STATE as snowflakeQueriesInitialState
} from 'app/store/snowflakeQueries/reducer.js';
import {
    type ExecutionTriggerStateSlice,
    executionTriggerReducer,
    INITIAL_STATE as executionTriggerInitialState
} from 'app/store/executionTrigger/reducer';
import {
    usageReportingReducer,
    INITIAL_STATE as usageReportingState
} from 'app/store/usageReporting/reducer.js';
import {
    ROIReportingReducer,
    INITIAL_STATE as ROIReportingState
} from 'app/store/ROIReporting/reducer.js';
import {
    multiJoinReducer,
    INITIAL_STATE as multiJoinInitialState
} from 'app/store/multiJoin/reducer.js';
import {
    projectModelsReducer,
    INITIAL_STATE as projectModelsInitialState
} from 'app/store/projectModels/reducer.js';
import {
    migrationReducer,
    INITIAL_STATE as migrationInitialState
} from 'app/store/migration/reducer.js';
import {
    inventoryReducer,
    INITIAL_STATE as inventoryInitialState
} from 'app/store/inventory/reducer.js';
import {
    qualityChecksReducer,
    INITIAL_STATE as qualityChecksInitialState,
    type QualityChecksSlice
} from 'app/store/qualityChecks/reducer';
import { SELECT_CELL } from 'app/store/types.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function selectedCell(state = null, action: any) {
    switch (action.type) {
        case SELECT_CELL:
            return action.payload;
        default:
            return state;
    }
}

enableMapSet();

export type RootState = {
    access: AccessStateSlice;
    assets: AssetsSlice;
    columnFilter: ColumnFilterSlice;
    columnManagement: ColumnManagementSlice;
    columnWidths: ColumnWidthState;
    dbt: DbtStateSlice;
    deploymentHistory: DeploymentHistoryStateSliceType;
    deployments: DeploymentsStateSlice;
    executionTrigger: ExecutionTriggerStateSlice;
    github: GithubStateSlice;
    healthcheck: HealthcheckSliceType;
    jobs: JobsReduxSlice;
    preferences: PreferencesSlice;
    projectHistory: ProjectHistorySlice;
    qualityChecks: QualityChecksSlice;
    snowflakeConfiguration: SnowflakeConfigurationSlice;
    sqlEditor: SqlEditorStateSlice;
    startup: StartupSlice;
    websocketStatus: WebsocketStatusSlice;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [index: string]: any;
};

export const ROOT_STATE: RootState = {
    access: accessInitialState,
    accessControls: accessControlsInitialState,
    addViews: addViewsInitialState,
    admin: adminInitialState,
    applicationConfig: applicationConfigInitialState,
    assets: assetsInitialState,
    attributes: attributesInitialState,
    blend: blendInitialState,
    materialize: materializeInitialState,
    columnFilter: columnFilterInitialState,
    columnManagement: columnManagementInitialState,
    columnStatistics: columnStatisticsInitialState,
    columnWidths: columnWidthsInitialState,
    dbt: dbtInitialState,
    deployView: deployViewInitialState,
    deploymentHistory: deploymentHistoryInitialState,
    deployments: deploymentsInitialState,
    downloadView: downloadViewInitialState,
    entities: entitiesInitialState,
    error: errorInitialState,
    exchangeSource: exchangeSourceInitialState,
    executionTrigger: executionTriggerInitialState,
    exploreData: exploreDataInitialState,
    extractJson: extractJsonInitialState,
    fileUpload,
    filter: filterInitialState,
    findDatasets: findDatasetsInitialState,
    flow: flowInitialState,
    formulaEditor: formulaEditorInitialState,
    github: githubInitialState,
    glossary: glossaryInitialState,
    healthcheck: healthcheckInitialState,
    integrations: integrationsInitialState,
    jobs: jobsInitialState,
    migration: migrationInitialState,
    inventory: inventoryInitialState,
    multiJoin: multiJoinInitialState,
    notebook: notebookInitialState,
    notifications: notificationsInitialState,
    operations: operationsInitialState,
    preferences: preferencesInitialState,
    projectHistory: projectHistoryInitialState,
    projectModels: projectModelsInitialState,
    projects: projectsInitialState,
    qualityChecks: qualityChecksInitialState,
    ROIReporting: ROIReportingState,
    schemaUsage: schemaUsageInitialState,
    search: searchInitialState,
    selectedCell,
    shares: sharesInitialState,
    snowflake: snowflakeInitialState,
    snowflakeConfiguration: snowflakeConfigurationInitialState,
    snowflakeQueries: snowflakeQueriesInitialState,
    sqlEditor: sqlEditorInitialState,
    startup: startupInitialState,
    summarize: summarizeInitialState,
    tableDetails: tableDetailsInitialState,
    transformSql: transformSqlInitialState,
    union: unionInitialState,
    usageReporting: usageReportingState,
    userAuth: userAuthInitialState,
    users: usersInitialState,
    view: viewInitialState,
    websocketStatus: websocketStatusInitialState
};

export type Reducers = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof RootState]: (state: RootState[K], action: any) => RootState[K];
};

export const reducers: Reducers = {
    access: accessReducer,
    accessControls: accessControlsReducer,
    addViews: addViewsReducer,
    admin: adminReducer,
    applicationConfig: applicationConfigReducer,
    assets: assetsReducer,
    attributes: attributesReducer,
    blend: blendReducer,
    materialize: materializeReducer,
    columnFilter: columnFilterReducer,
    columnManagement: columnManagementReducer,
    columnStatistics: columnStatisticsReducer,
    columnWidths: columnWidthsReducer,
    dbt: dbtReducer,
    deployView: deployViewReducer,
    deploymentHistory: deploymentHistoryReducer,
    deployments: deploymentsReducer,
    downloadView: downloadViewReducer,
    entities: entitiesReducer,
    error: errorReducer,
    exchangeSource: exchangeSourceReducer,
    executionTrigger: executionTriggerReducer,
    exploreData: exploreDataReducer,
    extractJson: extractJsonReducer,
    fileUpload,
    filter: filterReducer,
    findDatasets: findDatasetsReducer,
    flow: flowReducer,
    formulaEditor: formulaEditorReducer,
    github: githubReducer,
    glossary: glossaryReducer,
    healthcheck: healthcheckReducer,
    integrations: integrationsReducer,
    jobs: jobsReducer,
    migration: migrationReducer,
    inventory: inventoryReducer,
    multiJoin: multiJoinReducer,
    notebook: notebookReducer,
    notifications: notificationsReducer,
    operations: operationsReducer,
    preferences: preferencesReducer,
    projectHistory: projectHistoryReducer,
    projectModels: projectModelsReducer,
    projects: projectsReducer,
    qualityChecks: qualityChecksReducer,
    ROIReporting: ROIReportingReducer,
    schemaUsage: schemaUsageReducer,
    search: searchReducer,
    selectedCell,
    shares: sharesReducer,
    snowflake: snowflakeReducer,
    snowflakeConfiguration: snowflakeConfigurationReducer,
    snowflakeQueries: snowflakeQueriesReducer,
    sqlEditor: sqlEditorReducer,
    startup: startupReducer,
    summarize: summarizeReducer,
    tableDetails: tableDetailsReducer,
    transformSql: transformSqlReducer,
    union: unionReducer,
    usageReporting: usageReportingReducer,
    userAuth: userAuthReducer,
    users: usersReducer,
    view: viewReducer,
    websocketStatus: websocketStatusReducer
};
